// Förklaringslager: varför varje dokument behövs och hur det hänger ihop
// med övriga dokument i Bas-processen.

export interface DocExplanation {
  /** Kort motivering – varför dokumentet finns */
  why: string;
  /** Dokumentnummer som matar in underlag hit */
  buildsOn: string[];
  /** Dokumentnummer som tar emot resultatet härifrån */
  feeds: string[];
  /** Vem som ansvarar */
  owner: string;
  /** Konsekvens av att hoppa över steget */
  ifSkipped: string;
  /** Lagreferens */
  legal?: string[];
}

export const DOC_EXPLANATIONS: Record<string, DocExplanation> = {
  "10b": {
    why: "Samlar alla arbetsområdesutredningar i en gemensam redovisning så att Bas-P ser skillnader mellan områdena och kan ta ett gemensamt beslut i stället för att läsa varje utredning för sig.",
    buildsOn: ["10"],
    feeds: ["12", "30c"],
    owner: "Bas-P",
    ifSkipped: "Områden bedöms olika utan att någon upptäcker det, och arbetsmiljöplanen bygger på motstridiga underlag.",
    legal: ["AFS 2023:3"],
  },
  "30c": {
    why: "Slår ihop flera projektörers risksammanställningar och inlämningar till ett samordnat dokument där lika risker grupperas och olika bedömningar avgörs av Bas-P.",
    buildsOn: ["16", "30b", "31"],
    feeds: ["12", "20"],
    owner: "Bas-P",
    ifSkipped: "Samma risk beskrivs olika i flera dokument och entreprenören vet inte vilken bedömning som gäller.",
    legal: ["AFS 2023:3 kap. 4"],
  },
  "00": {
    why: "Ger hela projektet en gemensam bild av vilka dokument som ingår, i vilken ordning de tas fram och vem som äger vad.",
    buildsOn: [],
    feeds: ["01", "03"],
    owner: "Bas-P",
    ifSkipped: "Dokument tas fram i fel ordning och information tappas mellan skedena.",
    legal: ["AFS 2023:3"],
  },
  "01": {
    why: "Rutinen beskriver hur arbetsmiljöarbetet bedrivs systematiskt under planering och projektering – den är överordnad övriga mallar och visar att Bas-P arbetar strukturerat, samordnat och spårbart.",
    buildsOn: ["00"],
    feeds: ["10", "11", "12", "20", "25"],
    owner: "Bas-P",
    ifSkipped: "Arbetsmiljöarbetet blir personberoende och går inte att visa upp vid tillsyn eller revision.",
    legal: ["AFS 2023:1", "AFS 2023:3"],
  },
  "02": {
    why: "Säkerställer att byggnaden går att drifta, underhålla och städa säkert när den är färdig – bruksskedet måste beaktas redan i projekteringen.",
    buildsOn: ["03"],
    feeds: ["11", "12", "31", "32"],
    owner: "Bas-P",
    ifSkipped: "Framtida drift- och underhållsarbeten blir riskfyllda och dyra att rätta i efterhand.",
    legal: ["AFS 2023:3", "AFS 2023:11"],
  },
  "03": {
    why: "Storlek, komplexitet och riskprofil (SKR) styr hur omfattande arbetsmiljöarbetet behöver vara i just det här projektet.",
    buildsOn: [],
    feeds: ["10", "11", "12"],
    owner: "Bas-P",
    ifSkipped: "Insatsnivån blir godtycklig – antingen överarbetad eller för tunn för riskerna.",
  },
  "04": {
    why: "Förhandsanmälan till Arbetsmiljöverket är ett lagkrav när arbetet överstiger tröskelvärdena och ska anslås på arbetsplatsen.",
    buildsOn: ["03"],
    feeds: ["12"],
    owner: "Byggherre",
    ifSkipped: "Direkt lagbrott med sanktionsrisk, och AMP saknar en obligatorisk bilaga.",
    legal: ["AFS 2023:3 kap. 3"],
  },
  "05": {
    why: "Bas-P måste utses skriftligt av byggherren – uppdragsbekräftelsen visar vem som ansvarar och vilken omfattning uppdraget har.",
    buildsOn: ["00"],
    feeds: ["01", "12"],
    owner: "Byggherre",
    ifSkipped: "Ansvaret är oklart och byggherren behåller hela arbetsmiljöansvaret.",
    legal: ["AFS 2023:3 kap. 2"],
  },
  "10": {
    why: "Platsen avgör riskerna. Etablering, logistik, angränsande verksamhet och befintliga installationer måste kartläggas innan risker kan bedömas.",
    buildsOn: ["03"],
    feeds: ["11", "12", "26"],
    owner: "Bas-P",
    ifSkipped: "Risker som beror på platsen upptäcks först i produktion, när de är dyrast att hantera.",
    legal: ["AFS 2023:3", "AFS 2023:12"],
  },
  "11": {
    why: "Risksammanställningen är projektets samlade och spårbara register över identifierade risker, beslutade åtgärder, ansvariga och status.",
    buildsOn: ["10", "02", "03"],
    feeds: ["12", "13", "20", "24", "31"],
    owner: "Bas-P",
    ifSkipped: "Ingen kan visa vilka risker som är hanterade – AMP och arbetsberedningar saknar underlag.",
    legal: ["AFS 2023:3", "AFS 2023:12"],
  },
  "12": {
    why: "Arbetsmiljöplanen är det styrande dokumentet för arbetsmiljön under byggskedet och ska finnas tillgänglig på arbetsplatsen innan arbetet startar.",
    buildsOn: ["10", "11", "13", "15", "04"],
    feeds: ["20", "21", "24", "26"],
    owner: "Bas-P → Bas-U",
    ifSkipped: "Bygget får inte starta. AMP är ett formellt krav och saknad plan är sanktionsgrundande.",
    legal: ["AFS 2023:3 kap. 4"],
  },
  "13": {
    why: "De särskilda riskerna avgör om AMP måste upprättas och vilka åtgärder som är obligatoriska i planen.",
    buildsOn: ["10", "11"],
    feeds: ["12", "24"],
    owner: "Bas-P",
    ifSkipped: "AMP kan sakna de risker som lagstiftaren pekat ut som allvarligast.",
    legal: ["AFS 2023:3 bilaga"],
  },
  "14": {
    why: "Granskningen visar att handlingarna faktiskt är arbetsmiljögranskade innan de går vidare till utförandet.",
    buildsOn: ["11", "12"],
    feeds: ["20"],
    owner: "Bas-P",
    ifSkipped: "Fel i projekteringen följer med in i produktionen utan att någon fångat dem.",
  },
  "15": {
    why: "Gemensamma skydds- och ordningsregler gör att alla entreprenörer på arbetsplatsen jobbar efter samma spelregler.",
    buildsOn: ["10", "11"],
    feeds: ["12", "21"],
    owner: "Bas-P",
    ifSkipped: "Varje entreprenör tolkar reglerna själv och samordningen på plats brister.",
    legal: ["AFS 2023:3 kap. 4"],
  },
  "20": {
    why: "Överlämningen säkrar att Bas-U tar över kända risker, kvarstående frågor och förutsättningar – inget faller mellan stolarna.",
    buildsOn: ["11", "12", "14", "25"],
    feeds: ["21", "23", "24"],
    owner: "Bas-P / Bas-U",
    ifSkipped: "Bas-U börjar om från noll och projekteringens riskarbete går förlorat.",
    legal: ["AFS 2023:3"],
  },
  "21": {
    why: "Uppföljning på plats visar att de projekterade åtgärderna verkligen genomförs – annars stannar arbetsmiljöarbetet på papperet.",
    buildsOn: ["12", "15", "20"],
    feeds: ["23", "28", "30"],
    owner: "Bas-U",
    ifSkipped: "Ingen vet om skyddsåtgärderna finns i verkligheten.",
    legal: ["AFS 2023:1", "AFS 2023:3"],
  },
  "22": {
    why: "Byggherren och den framtida förvaltaren måste få med sig kvarstående risker samt drift- och underhållsunderlag.",
    buildsOn: ["02", "31", "21"],
    feeds: ["32"],
    owner: "Bas-U",
    ifSkipped: "Förvaltaren ärver okända risker utan att veta om dem.",
    legal: ["AFS 2023:3", "AFS 2023:11"],
  },
  "23": {
    why: "När projektering pågår parallellt med utförandet måste Bas-P och Bas-U mötas löpande och protokollföra beslut om risker och ändringar.",
    buildsOn: ["20"],
    feeds: ["11", "12", "25"],
    owner: "Bas-P / Bas-U",
    ifSkipped: "Ändringar i projekteringen når aldrig arbetsplatsen.",
  },
  "24": {
    why: "Arbetsberedningen bryter ned ett arbetsmoment till konkreta steg, risker och åtgärder som utförarna faktiskt kan följa.",
    buildsOn: ["11", "12", "13", "20"],
    feeds: ["21", "28"],
    owner: "Bas-U / entreprenör",
    ifSkipped: "Riskerna stannar på övergripande nivå och når aldrig den som utför arbetet.",
    legal: ["AFS 2023:3", "AFS 2023:1"],
  },
  "25": {
    why: "Q&A-loggen fångar frågor, oklarheter och beslut som påverkar risklogg, AMP och arbetsmiljödokumentation – med spårbarhet på vem som svarat och när.",
    buildsOn: ["11", "12"],
    feeds: ["11", "12", "20"],
    owner: "Bas-P / Bas-U",
    ifSkipped: "Beslut fattas muntligt och går inte att rekonstruera i efterhand.",
  },
  "26": {
    why: "Skedes- och APD-planen visar vad som sker var och när, vilka arbeten som krockar och vilka gemensamma skydd som krävs.",
    buildsOn: ["10", "12"],
    feeds: ["21", "23", "24"],
    owner: "Bas-U",
    ifSkipped: "Samtidiga arbeten krockar och gemensamma skyddsanordningar planeras inte.",
    legal: ["AFS 2023:3"],
  },
  "28": {
    why: "Tillbud och olyckor måste rapporteras, utredas och åtgärdas – och allvarliga händelser anmälas till Arbetsmiljöverket.",
    buildsOn: ["21", "24"],
    feeds: ["11", "12", "30"],
    owner: "Bas-U",
    ifSkipped: "Samma händelse upprepas och anmälningsplikten missas.",
    legal: ["AFS 2023:1"],
  },
  "27": {
    why: "Skyddsronden är den återkommande kontrollen på plats: Bas-U går arbetsplatsen med entreprenörer och skyddsombud, dokumenterar avvikelser och följer upp att de åtgärdas. Den överlämnande ronden verifierar att arbetsplatsen är säker inför bruksskedet.",
    buildsOn: ["12", "15", "21", "28"],
    feeds: ["28", "30", "33", "34"],
    owner: "Bas-U",
    ifSkipped: "Brister upptäcks för sent, den systematiska uppföljningen saknas och överlämningen till bruksskedet kan inte styrkas.",
    legal: ["AFS 2023:1", "AFS 2023:3"],
  },
  "30": {
    why: "Risksammanställningen för utförandet listar de risker som byggarbetet innebär – arbetsmoment, etablering och produktion – med de skyddsåtgärder som ska gälla. Den är underlaget till arbetsmiljöplanen och till arbetsberedningarna på plats.",
    buildsOn: ["02", "10", "11"],
    feeds: ["12", "21", "31"],
    owner: "Bas-P (projektörernas underlag)",
    ifSkipped: "Riskerna når aldrig arbetsmiljöplanen och de som bygger får inte veta vilka skyddsåtgärder som förutsatts.",
    legal: ["AFS 2023:3"],
  },

  "31": {
    why: "Risker som inte går att bygga bort måste dokumenteras för dem som ska drifta, underhålla, ändra eller riva anläggningen.",
    buildsOn: ["02", "11", "30"],
    feeds: ["22", "32"],
    owner: "Bas-P / Bas-U",
    ifSkipped: "Framtida arbeten utförs utan kännedom om kvarstående faror.",
    legal: ["AFS 2023:11"],
  },
  "33": {
    why: "Slutlig kontroll av att funktionskraven för bruksskedet (Dok 02) verkligen är uppfyllda i det färdiga byggnadsverket – underlaget som Bas-P lämnar över som del av arbetsmiljödokumentationen.",
    buildsOn: ["02", "31", "30"],
    feeds: ["22", "32"],
    owner: "Bas-P",
    ifSkipped: "Ingen vet om de projekterade lösningarna för drift och underhåll faktiskt byggdes – kvarstående brister upptäcks först i förvaltningen.",
    legal: ["AFS 2023:3", "AFS 2023:11"],
  },
  "34": {
    why: "Samlar hela arbetsmiljödokumentationen i ett dokument när projekteringen är klar: vad som lämnas över, vilka risker som kvarstår och vad den överlämnande skyddsronden ska kontrollera på plats.",
    buildsOn: ["30", "31", "33", "25"],
    feeds: ["22", "32"],
    owner: "Bas-P",
    ifSkipped: "Överlämningen blir en lös bunt handlingar utan sammanfattning – mottagaren ser inte vad som är kvar att hantera.",
    legal: ["AFS 2023:3", "AFS 2023:11"],
  },
  "32": {
    why: "Arbetsmiljödokumentationen ska följa byggnadsverket och bevaras hos byggherren för framtida arbeten.",
    buildsOn: ["22", "31"],
    feeds: [],
    owner: "Byggherre",
    ifSkipped: "Dokumentationen försvinner och kravet på bevarande uppfylls inte.",
    legal: ["AFS 2023:3", "AFS 2023:11"],
  },
  "06": {
    why: "Tidplanen avgör om arbetena hinner göras säkert och om två riskfyllda moment hamnar i samma utrymme samtidigt. Granskningen visar krockarna innan de sker på plats.",
    buildsOn: ["03", "10"],
    feeds: ["11", "12", "23", "24"],
    owner: "Bas-P",
    ifSkipped: "Krockande arbeten upptäcks först på bygget, när det bara går att lösa med stopp eller improviserade skydd.",
    legal: ["AFS 2023:3 kap. 4"],
  },
  "07": {
    why: "Boende, personal och förbipasserande påverkas av bygget. Här beskrivs hur de skyddas, informeras och hur tillfälliga vägar och tider hanteras.",
    buildsOn: ["10", "06"],
    feeds: ["11", "12", "15"],
    owner: "Bas-P",
    ifSkipped: "Tredje man rör sig oskyddad i eller intill arbetsområdet och ansvaret blir oklart när något händer.",
    legal: ["AFS 2023:3", "AFS 2023:11"],
  },
  "08": {
    why: "Asbest, PCB och andra hälsofarliga material måste vara inventerade innan rivning påbörjas, med provsvar, saneringsordning och avfallsdokumentation.",
    buildsOn: ["10"],
    feeds: ["11", "12", "24"],
    owner: "Bas-P",
    ifSkipped: "Rivning kan frigöra asbest eller PCB utan skydd – allvarlig hälsorisk och direkt lagbrott.",
    legal: ["AFS 2023:10", "AFS 2023:11"],
  },
  "09": {
    why: "Innan arbete på el, tryck eller gas ska anläggningen kopplas ifrån, låsas och kontrolleras spänningslös. Här dokumenteras varje frånkoppling med ansvarig.",
    buildsOn: ["10", "24"],
    feeds: ["21", "27"],
    owner: "Bas-U",
    ifSkipped: "Arbete kan påbörjas på spänningssatt eller trycksatt anläggning – risk för dödsolycka.",
    legal: ["ELSÄK-FS 2022:1", "AFS 2023:11"],
  },
  "15c": {
    why: "Varje person på arbetsplatsen ska ha fått introduktion till arbetsmiljöplanen och ordningsreglerna, på ett språk hen förstår. Registret visar vem, när och vad som gicks igenom.",
    buildsOn: ["12", "15"],
    feeds: ["21", "27"],
    owner: "Bas-U",
    ifSkipped: "Det går inte att visa att de som arbetar på platsen känner till riskerna och reglerna.",
    legal: ["AFS 2023:3 kap. 4", "AFS 2023:1"],
  },
  "16": {
    why: "Projektörerna redovisar de risker de inte kunnat bygga bort, per disciplin och skede. Bas-P kvitterar och för in dem i risksammanställningen och arbetsmiljöplanen.",
    buildsOn: ["03", "10"],
    feeds: ["11", "12", "31"],
    owner: "Projektör",
    ifSkipped: "Kvarvarande projekteringsrisker når aldrig arbetsmiljöplanen och upptäcks först i produktionen.",
    legal: ["AFS 2023:3 kap. 2", "AFS 2023:12"],
  },
  "00c": {
    why: "Ger en läsbar lägesbild av hela processen: vilka skeden som är klara, vad som pågår och vad som väntar.",
    buildsOn: ["00"],
    feeds: ["55"],
    owner: "Bas-P",
    ifSkipped: "Ingen ser helheten och statusfrågor besvaras med gissningar.",
    legal: ["AFS 2023:3"],
  },
  "17": {
    why: "Får in arbetsmiljökraven redan i förfrågningsunderlag och avtal, så att entreprenörer och projektörer prissätter och planerar rätt från början.",
    buildsOn: ["03", "50"],
    feeds: ["05", "18"],
    owner: "Byggherre",
    ifSkipped: "Kraven kommer först på bygget, när de kostar tid och pengar att lägga till.",
    legal: ["AFS 2023:3 kap. 2"],
  },
  "18": {
    why: "Beskriver hur projektören arbetar med arbetsmiljö: identifiera risker, projektera bort dem och redovisa det som blir kvar.",
    buildsOn: ["17"],
    feeds: ["16", "19"],
    owner: "Projektör",
    ifSkipped: "Varje projektör hittar på sitt eget arbetssätt och risker faller mellan disciplinerna.",
    legal: ["AFS 2023:3 kap. 2", "AFS 2023:12"],
  },
  "19": {
    why: "Intygar vad projektörsleveransen innehåller och gör den spårbar via verifieringskod, även för mottagare utan konto.",
    buildsOn: ["16", "18", "30b"],
    feeds: ["30c"],
    owner: "Projektör",
    ifSkipped: "Det går inte att visa vad som faktiskt levererades eller när.",
    legal: ["AFS 2023:3 kap. 2"],
  },
  "30b": {
    why: "Samlar riskerna som gäller själva byggskedet, skilt från de risker som lever kvar i bruksskedet.",
    buildsOn: ["10", "11", "16"],
    feeds: ["12", "24", "30c"],
    owner: "Bas-P",
    ifSkipped: "Bygg- och bruksrisker blandas ihop och entreprenören får en otydlig lista.",
    legal: ["AFS 2023:3", "AFS 2023:12"],
  },
  "06c": {
    why: "Ger en kort daglig lägesbild av pågående arbeten, aktuella risker och avvikelser på arbetsplatsen.",
    buildsOn: ["21", "26"],
    feeds: ["27", "28"],
    owner: "Bas-U",
    ifSkipped: "Ingen sammanhängande bild av vad som hände på plats vid en viss tidpunkt.",
    legal: ["AFS 2023:1"],
  },
  "24b": {
    why: "Ger skriftligt klartecken innan ett riskfyllt moment startar – beredning, kompetens och skydd är kontrollerade.",
    buildsOn: ["24", "29"],
    feeds: ["21"],
    owner: "Bas-U",
    ifSkipped: "Arbeten startar utan kontroll av att skyddsåtgärderna faktiskt finns på plats.",
    legal: ["AFS 2023:3 kap. 4"],
  },
  "29": {
    why: "Samlar entreprenörernas kvittenser på regler och risker samt intyg på behörigheter och utbildningar.",
    buildsOn: ["15", "15c"],
    feeds: ["24b"],
    owner: "Bas-U",
    ifSkipped: "Det går inte att visa vem som fått informationen eller vem som har rätt behörighet.",
    legal: ["AFS 2023:1", "AFS 2023:3 kap. 4"],
  },
  "55": {
    why: "Samlar byggherrens delegeringskedja, lagefterlevnad och status per skede i en rapport – beviset på utförd tillsyn.",
    buildsOn: ["51", "52", "53"],
    feeds: ["32"],
    owner: "Byggherre",
    ifSkipped: "Byggherren kan inte visa vid tillsyn att ansvaret följts upp.",
    legal: ["AFS 2023:3 kap. 2"],
  },
};


/** Slår upp förklaring för ett dokumentnummer, även för sammansatta nummer som "02 / 31". */
export const getExplanation = (docNumber?: string): DocExplanation | undefined => {
  if (!docNumber) return undefined;
  const first = docNumber.split("/")[0].trim();
  return DOC_EXPLANATIONS[first] ?? DOC_EXPLANATIONS[docNumber];
};
